import { z } from "zod";
import type { ActionOperation } from "./actions";
import type { AdvisorOperation } from "./advisor";
import type { BoardOperation } from "./boards";
import {
  type Actor,
  type CommittedDelta,
  type Operation,
  type ProposedDelta,
  ProposedDeltaSchema,
  type UserDelta,
  type UserHistory,
  type UserProposal,
  UserProposalSchema,
  type WorldHistory,
} from "./deltas";
import type { DistributionListOperation } from "./distribution-lists";
import type { GameMasterOperation } from "./game-master";
import {
  applyToIndexes,
  applyToView,
  type Clock,
  cloneIndexes,
  commit,
  emptyIndexes,
  validationEntryOf as entryOf,
  hasUsers,
  isPerspectiveView,
  isRoundOperation,
  isSpectatorView,
  type Landing,
  landingOf,
  type PerspectiveValidationIndex,
  type ProposingUser,
  reachedViewRefs,
  reactionKey,
  sameValue,
  type ThingValidationEntry,
  type ThreadValidationEntry,
  targetOf,
  USER_OPERATION_TYPES,
  type UserValidationIndex,
  userTarget,
  type ValidationIndexes,
  type ViewValidationIndex,
  viewFor,
} from "./indexes";
import type { JumpRequestOperation, RoundOperation } from "./narrative";
import type {
  AliasOperation,
  PerspectiveChangeOperation,
  PerspectiveOperation,
  PerspectiveSuggestionOperation,
  ViewRef,
} from "./perspectives";
import { shotCanvas } from "./reading";
import type { SkillOperation } from "./skills";
import { viewKey } from "./targets";
import {
  type Animated,
  BUILT_IN_STYLES,
  type CanvasThing,
  type CardThing,
  CHANGE_SCHEMAS,
  type Change,
  type EntryThing,
  isAround,
  isCover,
  isEdge,
  isTrack,
  type LineThing,
  type LinkThing,
  type PointThing,
  type ShapeGeometry,
  type ShapeThing,
  type TextThing,
  type Thing,
  type ThingKind,
  type Values,
} from "./things";
import type { ThreadOperation, ThreadSpeaker } from "./threads";
import {
  type Clarification,
  type Input,
  type Scene,
  sceneSeconds,
  type TimelineExtension,
  type TimelineOperation,
} from "./timeline";
import { messageNames } from "./words";

export const ErrorCodeSchema = z.enum([
  "invalid_proposal",
  "invalid_target",
  "unknown_reference",
  "already_exists",
  "wrong_actor",
  "invalid_lifecycle",
  "jump_active",
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const IssueSchema = z.strictObject({
  code: ErrorCodeSchema,
  path: z.array(z.union([z.string(), z.number()])),
  message: z.string(),
});
export type Issue = z.infer<typeof IssueSchema>;

/**
 * On success, the indexes with every delta applied, and the views each delta reached, in order.
 * On failure, every issue, each path starting with the delta's position.
 */
export type ValidationResult =
  | { ok: true; indexes: ValidationIndexes; reached: ViewRef[][] }
  | { ok: false; issues: Issue[] };

/** A whole record, or one user's copy of it. */
export type RecordBatch =
  | { type: "whole"; deltas: WorldHistory }
  | { type: "user"; user: ProposingUser; deltas: UserHistory };

/** Deltas of the whole record or of one user, each list committed or proposed. */
export type DeltaBatch =
  | { type: "whole"; deltas: WorldHistory | readonly ProposedDelta[] }
  | { type: "user"; user: ProposingUser; deltas: UserHistory | readonly UserProposal[] };

/**
 * The most a delta may take written out as JSON, in UTF-8 bytes. A delta is stored, sent and read
 * whole, so anything bigger is split into several, such as a starting map into several orders.
 */
export const MAX_DELTA_BYTES = 1_000_000;

const utf8 = new TextEncoder();

export interface ValidateOptions {
  /**
   * Apply the deltas to `indexes` itself rather than a copy. A rejected batch then rebuilds them
   * from their record.
   */
  mutate?: boolean;
}

/**
 * Checks deltas one by one, each against the indexes with the ones before it applied. One issue
 * anywhere rejects them all.
 *
 * Indexes of the whole record run whole-record validation: its own rules, then partial-record
 * validation in every view and user the delta lands in. Indexes of a user's copy run
 * partial-record validation alone, in that user's view or user.
 */
export function validate(
  indexes: ValidationIndexes,
  batch: DeltaBatch,
  options: ValidateOptions = {},
): ValidationResult {
  const { mutate = false } = options;
  const { copyFor } = indexes;
  if (copyFor && batch.type === "whole")
    throw new Error("A user's copy of the record checks only that user's deltas.");
  const checked: (ProposedDelta | UserDelta | UserProposal)[] = [];
  for (const [index, delta] of batch.deltas.entries()) {
    const bytes = utf8.encode(JSON.stringify(delta)).length;
    if (bytes > MAX_DELTA_BYTES) {
      return {
        ok: false,
        issues: [
          {
            code: "invalid_proposal",
            path: [index],
            message: `A delta is at most ${MAX_DELTA_BYTES.toLocaleString("en")} bytes as JSON, and this one is ${bytes.toLocaleString("en")}: split it into several.`,
          },
        ],
      };
    }
    if (isCommitted(delta)) {
      checked.push(delta);
      continue;
    }
    const parsed =
      batch.type === "whole"
        ? ProposedDeltaSchema.safeParse(delta)
        : copyFor
          ? UserProposalSchema.safeParse(delta)
          : UserProposalSchema.transform(data => fillUserProposal(data, batch.user)).safeParse(
              delta,
            );
    if (!parsed.success) {
      return {
        ok: false,
        issues: parsed.error.issues.map(issue => ({
          code: "invalid_proposal",
          path: [index, ...issue.path.map(segment => (typeof segment === "symbol" ? "" : segment))],
          message: issue.message,
        })),
      };
    }
    checked.push(parsed.data);
  }

  const working = mutate ? indexes : cloneIndexes(indexes);
  const reached: ViewRef[][] = [];
  for (const [index, delta] of checked.entries()) {
    const admission = working.copyFor
      ? admitToCopy(working, working.copyFor, delta.operation, isCommitted(delta))
      : admitToWholeRecord(working, wholeRecordDelta(delta));
    if (!admission.ok) {
      if (mutate && index > 0) Object.assign(indexes, rebuilt(indexes));
      return {
        ok: false,
        issues: admission.issues.map(issue => ({ ...issue, path: [index, ...issue.path] })),
      };
    }
    reached.push(admission.reached);
  }
  return { ok: true, indexes: working, reached };
}

/**
 * Builds the indexes for a record from an empty world. A whole record passes both validations, and
 * a user's copy passes partial-record validation.
 */
export function validateRecord(record: RecordBatch): ValidationResult {
  const user = record.type === "user" ? record.user : undefined;
  return validate(emptyIndexes(record.deltas, user), record);
}

function isCommitted(delta: object): delta is CommittedDelta | UserDelta {
  return "revision" in delta;
}

function wholeRecordDelta(delta: ProposedDelta | UserDelta | UserProposal): ProposedDelta {
  if ("target" in delta) return delta;
  throw new Error("The whole record's indexes check a user's proposals, not their copy.");
}

function rebuilt(indexes: ValidationIndexes): ValidationIndexes {
  const fresh = emptyIndexes(indexes.record, indexes.copyFor);
  for (const delta of indexes.record) {
    applyToIndexes(fresh, { target: targetOf(delta, indexes.copyFor), operation: delta.operation });
  }
  return fresh;
}

/**
 * A user's proposal as the game server commits it. The actor is the user's perspective, or the user
 * for what reaches users and for a user with no perspective. The target is the perspective or the
 * user.
 */
export function fillUserProposal(proposal: UserProposal, user: ProposingUser): ProposedDelta {
  const { operation } = proposal;
  const actor: Actor =
    USER_OPERATION_TYPES.has(operation.type) || !user.perspectiveHandle
      ? { type: "user", userId: user.userId }
      : { type: "perspective", handle: user.perspectiveHandle };
  const target = userTarget(operation, user);
  return { actor, responsibleUser: user.userId, target, ...proposal };
}

type Admission = { ok: true; reached: ViewRef[] } | { ok: false; issues: Issue[] };

/**
 * Whole-record validation. Its own rules read anything and can only reject. Then the delta must
 * pass partial-record validation in every copy it lands in, a new perspective's view built by
 * replaying its fork's copies as its users receive them.
 */
function admitToWholeRecord(indexes: ValidationIndexes, delta: ProposedDelta): Admission {
  const check = new WholeRecordCheck(indexes, delta);
  const { operation } = delta;
  checkInWholeRecord(check, operation);
  if (check.issues.length > 0) return { ok: false, issues: check.issues };
  const landing = landingOf(indexes, delta.target, operation, (view, copy) =>
    replayCopy(check, view, copy),
  );
  if (check.issues.length === 0) checkLanding(check, landing, operation);
  if (check.issues.length > 0) return { ok: false, issues: check.issues };
  commit(indexes, operation, landing);
  return { ok: true, reached: landing.refs };
}

/** Partial-record validation alone, in the user's own view or user. */
function admitToCopy(
  indexes: ValidationIndexes,
  user: ProposingUser,
  operation: Operation,
  committed: boolean,
): Admission {
  const check = new Check(committed);
  const landing = landingOf(indexes, userTarget(operation, user), operation);
  checkLanding(check, landing, operation);
  if (check.issues.length > 0) return { ok: false, issues: check.issues };
  commit(indexes, operation, landing);
  return { ok: true, reached: landing.refs };
}

function checkLanding(check: Check, landing: Landing, operation: Operation): void {
  for (const view of landing.views) checkInView(check, view, operation);
  for (const user of landing.users) checkForUser(check, user, operation);
}

function replayCopy(check: Check, view: PerspectiveValidationIndex, operation: Operation): void {
  if (check.issues.length === 0) {
    const replayed = new Check(true);
    checkInView(replayed, view, operation);
    const [issue] = replayed.issues;
    if (issue) {
      check.report(
        "invalid_lifecycle",
        ["operation", "fork"],
        `A copy from the forked view fails partial-record validation in the new perspective: ${issue.message}`,
      );
    }
  }
  applyToView(view, operation);
}

type Path = (string | number)[];

/** Collects issues. Its checks read one view or one user. */
class Check {
  readonly issues: Issue[] = [];

  /**
   * Whether what it checks is in the record already, written under the rules of its day: a rule
   * newer than the record looks only at what is proposed.
   */
  constructor(readonly committed = false) {}

  report(code: ErrorCode, path: Path, message: string): void {
    const same = this.issues.some(
      issue => issue.code === code && issue.message === message && sameValue(issue.path, path),
    );
    if (!same) this.issues.push({ code, path, message });
  }

  requireEventId(view: ViewValidationIndex, eventId: string | undefined, path: Path): void {
    if (eventId === undefined || view.eventIds.has(eventId)) return;
    this.report(
      "unknown_reference",
      path,
      `No event "${eventId}" in this view: what arrives with an event comes after its scene.`,
    );
  }

  /** A canvas of the view's that the map draws. */
  requireCanvas(view: ViewValidationIndex, id: string, path: Path): void {
    const canvas = view.things.get(id);
    if (canvas?.kind !== "canvas") {
      this.report("unknown_reference", path, `Canvas "${id}" is not on this view's map.`);
    } else if (canvas.arrangement !== undefined) {
      this.report("invalid_proposal", path, `"${id}" is a board, which has no extent: give a map.`);
    }
  }

  requireShapes(view: ViewValidationIndex, ids: readonly string[], path: Path): void {
    for (const [index, id] of ids.entries()) {
      if (view.things.get(id)?.kind === "shape") continue;
      this.report(
        "unknown_reference",
        [...path, index],
        `Shape "${id}" is not on this view's map.`,
      );
    }
  }

  requireNew(entries: { has(id: string): boolean }, id: string, path: Path, noun: string): boolean {
    if (!entries.has(id)) return true;
    this.report("already_exists", path, `${noun} "${id}" already exists.`);
    return false;
  }

  requireListed(ids: { has(id: string): boolean }, id: string, path: Path, noun: string): void {
    if (ids.has(id)) return;
    this.report("unknown_reference", path, `${noun} "${id}" does not exist.`);
  }

  requireExisting<Entry>(
    entries: Map<string, Entry>,
    id: string,
    path: Path,
    noun: string,
  ): Entry | null {
    const entry = entries.get(id);
    if (entry) return entry;
    this.report("unknown_reference", path, `${noun} "${id}" does not exist.`);
    return null;
  }

  requireNoJump(view: ViewValidationIndex): void {
    if (view.clock?.jumping !== true) return;
    this.report("jump_active", [], "This operation is not allowed while a jump is active.");
  }

  requireJump(view: ViewValidationIndex): void {
    if (view.clock?.jumping === true) return;
    this.report("invalid_lifecycle", [], "This operation requires an active jump.");
  }
}

// Partial-record validation: one operation against one copy, a view or a user. It never reads the
// actor, the responsible user, or the target.

function checkInView(check: Check, view: ViewValidationIndex, operation: Operation): void {
  if (isRoundOperation(operation)) {
    checkClock(check, view, operation);
    return;
  }
  switch (operation.type) {
    case "perspective.set":
    case "perspective.update":
    case "perspective.updateUsers":
    case "perspective.unset":
      checkPerspectiveInView(check, view, operation);
      return;
    case "jump.requested":
    case "jump.requestWithdrawn":
      checkJumpRequestInView(check, view, operation);
      return;
    case "perspectiveSuggestion.set":
    case "perspectiveSuggestion.update":
    case "perspectiveSuggestion.unset":
      checkSuggestionInView(check, view, operation);
      return;
    case "alias.set":
    case "alias.unset":
      checkAliasInView(check, view, operation);
      return;
    case "timeline.extended":
    case "timeline.clarified":
      checkWriteInView(check, view, operation);
      return;
    case "scene.shown":
      checkSceneShownInView(check, view, operation.scene);
      return;
  }
  if (!isPerspectiveView(view)) {
    check.report("invalid_target", [], "Only a perspective's view takes this operation.");
    return;
  }
  switch (operation.type) {
    case "input.asked":
      checkInputAskedInView(check, view, operation.input);
      return;
    case "input.answered":
    case "input.withdrawn":
      checkInputInView(check, view, operation);
      return;
    case "jump.intervened":
      check.requireJump(view);
      return;
    case "thread.create":
    case "thread.append":
    case "thread.update":
    case "thread.pollResponded":
    case "thread.pollClosed":
    case "thread.reactionSet":
    case "thread.reactionUnset":
    case "thread.pinsSet":
      checkThreadInView(check, view, operation);
      return;
    case "action.submitted":
    case "action.edited":
    case "action.withdrawn":
      checkActionInView(check, view, operation);
      return;
    case "board.arranged":
      checkBoardInView(check, view, operation);
      return;
  }
}

function checkForUser(check: Check, user: UserValidationIndex, operation: Operation): void {
  switch (operation.type) {
    case "perspectiveChange.requested":
      if (user.perspectiveChange) {
        check.report(
          "invalid_lifecycle",
          ["operation"],
          "This user is already waiting on a perspective change.",
        );
        return;
      }
      check.requireNew(
        user.perspectiveChangeIds,
        operation.id,
        ["operation", "id"],
        "Perspective change",
      );
      return;
    case "perspectiveChange.withdrawn":
    case "perspectiveChange.approved":
    case "perspectiveChange.refused":
      if (user.perspectiveChange?.id !== operation.id) {
        check.report(
          "invalid_lifecycle",
          ["operation", "id"],
          "This user is not waiting on that change.",
        );
      }
      return;
    case "advisor.entryAdded":
      check.requireNew(
        user.advisorEntryIds,
        operation.entry.id,
        ["operation", "entry", "id"],
        "Advisor entry",
      );
      return;
  }
}

function checkPerspectiveInView(
  check: Check,
  view: ViewValidationIndex,
  operation: PerspectiveOperation,
): void {
  if (!isPerspectiveView(view)) {
    check.report("invalid_target", [], "Only a perspective's view takes this operation.");
    return;
  }
  switch (operation.type) {
    case "perspective.set": {
      const { startingView } = operation.perspective;
      if (startingView) {
        check.requireCanvas(view, startingView.canvasId, [
          "operation",
          "perspective",
          "startingView",
          "canvasId",
        ]);
      }
      return;
    }
    case "perspective.update": {
      const record = view.perspective;
      if (!record) {
        check.report("unknown_reference", [], "This perspective is not set.");
        return;
      }
      check.requireEventId(view, operation.eventId, ["operation", "eventId"]);
      const { startingView } = operation.changes;
      if (startingView) {
        check.requireCanvas(view, startingView.canvasId, [
          "operation",
          "changes",
          "startingView",
          "canvasId",
        ]);
      }
      return;
    }
    case "perspective.updateUsers": {
      const record = view.perspective;
      if (!record) {
        check.report("unknown_reference", [], "This perspective is not set.");
        return;
      }
      const { userIds } = operation;
      const path = ["operation", "userIds"];
      if (new Set(userIds).size < userIds.length) {
        check.report("invalid_proposal", path, "A user is listed twice.");
        return;
      }
      if (userIds.every(userId => record.userIds.has(userId))) return;
      if (view.clock?.jumping) {
        check.report(
          "invalid_lifecycle",
          path,
          "A user can join a perspective only between jumps.",
        );
      }
      return;
    }
    case "perspective.unset":
      if (!view.perspective) {
        check.report("unknown_reference", [], "This perspective is not set.");
      }
      return;
  }
}

/** A perspective asks for the next jump between jumps, once, and takes back only its ask. */
function checkJumpRequestInView(
  check: Check,
  view: ViewValidationIndex,
  operation: JumpRequestOperation,
): void {
  if (!isPerspectiveView(view)) {
    check.report("invalid_target", [], "Only a perspective's view takes this operation.");
  } else if (view.clock?.jumping) {
    check.report("jump_active", ["operation"], "Jumps are asked for between jumps.");
  } else if (operation.type === "jump.requested" && view.jumpRequest) {
    check.report(
      "invalid_lifecycle",
      ["operation"],
      "This perspective has already asked for the jump.",
    );
  } else if (operation.type === "jump.requestWithdrawn" && !view.jumpRequest) {
    check.report(
      "invalid_lifecycle",
      ["operation"],
      "This perspective has not asked for the jump.",
    );
  }
}

function checkSuggestionInView(
  check: Check,
  view: ViewValidationIndex,
  operation: PerspectiveSuggestionOperation,
): void {
  if (!isSpectatorView(view)) {
    check.report("invalid_target", [], "Only the spectator's view takes perspective suggestions.");
    return;
  }
  const suggestions = view.perspectiveSuggestions;
  if (operation.type === "perspectiveSuggestion.set") {
    const { id, startingView, shapeIds } = operation.perspectiveSuggestion;
    const path = ["operation", "perspectiveSuggestion"];
    check.requireNew(suggestions, id, [...path, "id"], "Perspective suggestion");
    check.requireCanvas(view, startingView.canvasId, [...path, "startingView", "canvasId"]);
    check.requireShapes(view, shapeIds ?? [], [...path, "shapeIds"]);
    return;
  }
  if (!suggestions.has(operation.id)) {
    check.report(
      "unknown_reference",
      ["operation", "id"],
      `Perspective suggestion "${operation.id}" does not exist.`,
    );
    return;
  }
  if (operation.type === "perspectiveSuggestion.update") {
    const { startingView, shapeIds } = operation.changes;
    const path = ["operation", "changes"];
    if (startingView) {
      check.requireCanvas(view, startingView.canvasId, [...path, "startingView", "canvasId"]);
    }
    if (shapeIds) check.requireShapes(view, shapeIds, [...path, "shapeIds"]);
  }
}

function checkAliasInView(check: Check, view: ViewValidationIndex, operation: AliasOperation) {
  check.requireEventId(view, operation.eventId, ["operation", "eventId"]);
  if (operation.type === "alias.unset") {
    check.requireListed(view.aliases, operation.handle, ["operation", "handle"], "The alias for");
  }
}

/**
 * A write's partial-record validation. Nothing is written on the timeline of a perspective waiting
 * on an input. An extension continues the timeline from where it ends, and a clarification writes
 * where it ends: what it places is new to the map, what it changes is on it,
 * every reference names a thing of the right kind on the same canvas, and every tick it writes falls
 * in its stretch, the keys of a thing it places running only up to the thing's first change.
 */
function checkWriteInView(
  check: Check,
  view: ViewValidationIndex,
  operation: Extract<TimelineOperation, { type: "timeline.extended" | "timeline.clarified" }>,
) {
  if (isPerspectiveView(view) && view.awaits) {
    check.report(
      "invalid_lifecycle",
      ["operation"],
      "This perspective waits on an input, whose answer may change what happens: write on its timeline once the input is answered or withdrawn.",
    );
  }
  const start = view.end;
  const extension = operation.type === "timeline.extended" ? operation.extension : null;
  const write: TimelineExtension | Clarification =
    operation.type === "timeline.extended" ? operation.extension : operation.clarification;
  const at: Path = ["operation", extension ? "extension" : "clarification"];
  let window: Window = { from: start, to: start, of: "the clarification, where the timeline ends" };
  if (extension) {
    if (extension.end <= start) {
      check.report(
        "invalid_proposal",
        [...at, "end"],
        `The timeline ends at ${start}, and an extension moves it on: "end" is later than ${start}. What is written where the timeline ends is a clarification.`,
      );
    }
    window = {
      from: start,
      to: Math.max(start, extension.end),
      of: "the extension, from where the timeline ends to its `end`",
    };
  }
  const { things = [], changes = [], labels = [] } = write;
  const placed = new Map<string, ThingValidationEntry>();
  const restyled = new Map<string, string[]>();
  const thingAt = (id: string) => placed.get(id) ?? view.things.get(id);
  const stylesOf = (canvas: string) => restyled.get(canvas) ?? thingAt(canvas)?.styles ?? [];
  const on = { thingAt, stylesOf };
  placeThings(check, view, things, placed, [...at, "things"]);
  const firstChange = new Map<string, number>();
  for (const change of changes) {
    const styles = (change as { styles?: unknown }).styles;
    if (typeof styles === "object" && styles !== null) restyled.set(change.id, Object.keys(styles));
    const tick = change.at ?? window.from;
    firstChange.set(change.id, Math.min(firstChange.get(change.id) ?? tick, tick));
  }
  for (const [index, thing] of things.entries()) {
    const path: Path = [...at, "things", index];
    const changed = firstChange.get(thing.id);
    const keyed: Window =
      changed === undefined
        ? window
        : { from: window.from, to: changed, of: "the thing's stretch up to its first change" };
    checkThing(check, thing, on, keyed, path, true);
    requireMoments(check, thing, window, path);
  }
  checkChanges(check, changes, on, window, [...at, "changes"]);
  for (const [index, label] of labels.entries()) {
    requireWithin(check, label.tick, window, [...at, "labels", index, "tick"]);
  }
}

/** A scene shown on its own: new to the view, during a jump, and showing only what the timeline holds. */
function checkSceneShownInView(check: Check, view: ViewValidationIndex, scene: Scene) {
  check.requireJump(view);
  checkShownScene(check, view, scene, ["operation", "scene"]);
}

/** An input asked of a perspective that waits on none, during a jump, a reply in an open thread of its own. */
function checkInputAskedInView(check: Check, view: PerspectiveValidationIndex, input: Input) {
  check.requireJump(view);
  const at: Path = ["operation", "input"];
  if (view.awaits) {
    check.report(
      "invalid_lifecycle",
      at,
      "This perspective already waits on an input nobody has answered: withdraw it first.",
    );
  }
  if (input.type !== "reply") return;
  const thread = view.threads.get(input.threadId);
  if (!thread) {
    check.report(
      "unknown_reference",
      [...at, "threadId"],
      `Thread "${input.threadId}" does not exist.`,
    );
  } else if (thread.status !== "open") {
    check.report("invalid_lifecycle", [...at, "threadId"], "The thread is archived.");
  }
}

/**
 * One scene: the ticks it shows within the written timeline, and everything keyed in its seconds,
 * its tick, its camera, its things and its dims, within them. A thing of its own may give an
 * `until` past the scene's end; it leaves as the scene ends all the same.
 */
function checkScene(
  check: Check,
  scene: Scene,
  written: number,
  on: Parameters<typeof checkThing>[2],
  at: Path,
) {
  const { thingAt, stylesOf } = on;
  const window: Window = { from: 0, to: sceneSeconds(scene), of: "the scene's seconds" };
  const ticks = isTrack(scene.tick)
    ? scene.tick.keys.map((key, index) => ({
        value: key.value,
        path: [...at, "tick", "keys", index, "value"],
      }))
    : [{ value: scene.tick, path: [...at, "tick"] }];
  for (const tick of ticks) {
    if (tick.value > written + 1e-9) {
      check.report(
        "invalid_proposal",
        tick.path,
        `The timeline is written up to tick ${written}: a scene shows only what it holds.`,
      );
    }
  }
  if (isTrack(scene.tick)) requireKeys(check, scene.tick.keys, window, [...at, "tick", "keys"]);
  const shown = new Map<string, ThingValidationEntry>();
  const seenAt = (id: string) => shown.get(id) ?? thingAt(id);
  const shownOn = {
    thingAt: seenAt,
    stylesOf: (canvas: string) => shown.get(canvas)?.styles ?? stylesOf(canvas),
  };
  const own = scene.things ?? [];
  for (const [thingIndex, thing] of own.entries()) {
    if (thingAt(thing.id) || shown.has(thing.id)) {
      check.report(
        "already_exists",
        [...at, "things", thingIndex, "id"],
        `"${thing.id}" is already on the map; a scene's own things take ids of their own.`,
      );
      continue;
    }
    shown.set(thing.id, entryOf(thing));
  }
  if (scene.camera) {
    const path: Path = [...at, "camera", "shots"];
    const moments: { at: number; path: Path }[] = [];
    for (const [shotIndex, shot] of scene.camera.shots.entries()) {
      const shotPath: Path = [...path, shotIndex];
      const id = shotCanvas(shot);
      const canvas = seenAt(id);
      if (canvas?.kind !== "canvas") {
        check.report(
          "unknown_reference",
          [...shotPath, "map" in shot ? "map" : "board"],
          `Canvas "${id}" is on neither the map nor among the scene's own things.`,
        );
      } else if ("map" in shot && canvas.arrangement !== undefined) {
        check.report(
          "invalid_proposal",
          [...shotPath, "map"],
          `"${id}" is a board: a shot of it is { "board": "${id}", "at": ... }.`,
        );
      } else if ("board" in shot && canvas.arrangement === undefined) {
        check.report(
          "invalid_proposal",
          [...shotPath, "board"],
          `"${id}" is a map: a shot of it is { "map": "${id}", "keys": [...] }.`,
        );
      }
      if ("board" in shot) {
        moments.push({ at: shot.at, path: [...shotPath, "at"] });
        continue;
      }
      for (const [keyIndex, key] of shot.keys.entries()) {
        const keyPath: Path = [...shotPath, "keys", keyIndex];
        moments.push({ at: key.at, path: [...keyPath, "at"] });
        if (key.extent.southwest[1] >= key.extent.northeast[1]) {
          check.report(
            "invalid_proposal",
            [...keyPath, "extent"],
            "An extent's south-west corner lies south of its north-east corner.",
          );
        }
      }
    }
    let last = Number.NEGATIVE_INFINITY;
    for (const moment of moments) {
      requireWithin(check, moment.at, window, moment.path);
      if (moment.at < last) {
        check.report(
          "invalid_proposal",
          moment.path,
          "Shots and their keys go in order along the clock.",
        );
      }
      last = moment.at;
    }
  }
  for (const [thingIndex, thing] of own.entries()) {
    const thingPath: Path = [...at, "things", thingIndex];
    checkThing(check, thing, shownOn, window, thingPath, true);
    requireMoments(
      check,
      { from: thing.from, until: thing.until },
      { ...window, to: Infinity },
      thingPath,
    );
    if (thing.from !== undefined) requireWithin(check, thing.from, window, [...thingPath, "from"]);
  }
  for (const [index, dim] of (scene.dims ?? []).entries()) {
    const dimPath: Path = [...at, "dims", index];
    requireWithin(check, dim.from, window, [...dimPath, "from"]);
    requireWithin(check, dim.until, { ...window, to: Infinity }, [...dimPath, "until"]);
  }
  for (const [index, spotlight] of (scene.spotlights ?? []).entries()) {
    const spotlightPath: Path = [...at, "spotlights", index];
    requireWithin(check, spotlight.from, window, [...spotlightPath, "from"]);
    requireWithin(check, spotlight.until, { ...window, to: Infinity }, [...spotlightPath, "until"]);
    for (const [pointIndex, id] of spotlight.points.entries()) {
      if (seenAt(id)?.kind !== "point") {
        check.report(
          "unknown_reference",
          [...spotlightPath, "points", pointIndex],
          `No point "${id}" on the map or among the scene's own things.`,
        );
      }
    }
  }
}

/** Each of a scene's events new to the view, and to the scene's others. */
function requireNewEvents(check: Check, view: ViewValidationIndex, scene: Scene, at: Path) {
  const written = new Set<string>();
  for (const [index, event] of scene.events.entries()) {
    if (view.eventIds.has(event.id) || written.has(event.id)) {
      check.report(
        "already_exists",
        [...at, "events", index, "id"],
        `Event "${event.id}" is already in this view.`,
      );
    }
    written.add(event.id);
  }
}

/** A scene a message, an answer or its own delta shows: new to the view, over the timeline as it is written. */
function checkShownScene(check: Check, view: ViewValidationIndex, scene: Scene, at: Path) {
  if (view.sceneIds.has(scene.id)) {
    check.report("already_exists", [...at, "id"], `Scene "${scene.id}" is already in this view.`);
  }
  requireNewEvents(check, view, scene, at);
  const thingAt = (id: string) => view.things.get(id);
  checkScene(
    check,
    scene,
    view.end,
    { thingAt, stylesOf: canvas => thingAt(canvas)?.styles ?? [] },
    at,
  );
}

/** Things a write places: each new to the map and to what else it places. */
function placeThings(
  check: Check,
  view: ViewValidationIndex,
  things: readonly Thing[],
  placed: Map<string, ThingValidationEntry>,
  path: Path,
) {
  for (const [index, thing] of things.entries()) {
    if (view.things.has(thing.id) || placed.has(thing.id)) {
      check.report(
        "already_exists",
        [...path, index, "id"],
        `"${thing.id}" is already on the map.`,
      );
      continue;
    }
    placed.set(thing.id, entryOf(thing));
  }
}

/**
 * Changes a write makes: each to a thing on the map or one the write places, at most once at each
 * tick, in that thing's own terms, its keys and `until` from its `at` to the thing's next change.
 */
function checkChanges(
  check: Check,
  changes: readonly Change[],
  on: Parameters<typeof checkThing>[2],
  window: Window,
  path: Path,
) {
  const ticks = new Map<string, number[]>();
  for (const change of changes) {
    ticks.set(change.id, [...(ticks.get(change.id) ?? []), change.at ?? window.from]);
  }
  const changed = new Set<string>();
  for (const [index, change] of changes.entries()) {
    const at: Path = [...path, index];
    const entry = on.thingAt(change.id);
    if (!entry) {
      check.report(
        "unknown_reference",
        [...at, "id"],
        `"${change.id}" is not on the map, and the write places no thing by that id.`,
      );
      continue;
    }
    const from = change.at ?? window.from;
    if (change.at !== undefined) requireWithin(check, change.at, window, [...at, "at"]);
    const next = Math.min(...(ticks.get(change.id) ?? []).filter(tick => tick > from));
    const own: Window = {
      from,
      to: Math.min(window.to, next),
      of: `the change, from its \`at\` to ${Number.isFinite(next) ? "the thing's next change" : "the write's end"}`,
    };
    requireMoments(check, change as { until?: number }, own, at);
    if (changed.has(`${change.id}@${from}`)) {
      check.report(
        "invalid_proposal",
        [...at, "id"],
        `"${change.id}" is changed twice at ${from}: give each change its own \`at\`.`,
      );
      continue;
    }
    changed.add(`${change.id}@${from}`);
    const parsed = CHANGE_SCHEMAS[entry.kind].safeParse(change);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        check.report("invalid_proposal", [...at, ...issue.path.map(String)], issue.message);
      }
      continue;
    }
    const given = change as Partial<Thing> & { canvas?: string };
    const merged = {
      ...change,
      kind: entry.kind,
      ...(entry.canvas !== undefined ? { canvas: given.canvas ?? entry.canvas } : {}),
    } as Partial<Thing> & { kind: ThingKind };
    checkThing(check, merged, on, own, at, false);
  }
}

/** One thing an order places, or what one change gives, checked against the map and the order. */
function checkThing(
  check: Check,
  thing: Partial<Thing> & { kind: ThingKind },
  on: {
    thingAt: (id: string) => ThingValidationEntry | undefined;
    /** The names of a canvas's own styles, as the order leaves them. */
    stylesOf: (canvas: string) => string[];
  },
  window: Window,
  path: Path,
  placed: boolean,
): void {
  const { thingAt, stylesOf } = on;
  requireTracks(check, thing, window, path);
  if (thing.kind === "category") return;
  if (thing.kind === "canvas") {
    const { category } = thing as Partial<CanvasThing>;
    if (category !== undefined && thingAt(category)?.kind !== "category") {
      check.report(
        "unknown_reference",
        [...path, "category"],
        `No category "${category}" on the map or in the order.`,
      );
    }
    return;
  }
  const canvas = (thing as { canvas?: string }).canvas;
  const surface = canvas === undefined ? undefined : thingAt(canvas);
  if (canvas !== undefined && surface?.kind !== "canvas") {
    check.report(
      "unknown_reference",
      [...path, "canvas"],
      `Canvas "${canvas}" is on neither the map nor the order.`,
    );
  } else if (surface) {
    const board = surface.arrangement !== undefined;
    if (board !== (thing.kind === "entry" || thing.kind === "link")) {
      check.report(
        "invalid_proposal",
        [...path, "canvas"],
        board
          ? `"${canvas}" is a board: only entries and links go on it.`
          : `"${canvas}" is a map: entries and links go on a board.`,
      );
    }
  }
  const requireOn = (id: string, kinds: ThingKind[], at: Path) => {
    const entry = thingAt(id);
    if (!entry || !kinds.includes(entry.kind)) {
      check.report(
        "unknown_reference",
        at,
        `No ${kinds.join(" or ")} "${id}" on the map or in the order.`,
      );
    } else if (entry.canvas !== canvas) {
      check.report("unknown_reference", at, `"${id}" is on another canvas.`);
    }
  };
  const requireStyle = (name: string | undefined, at: Path) => {
    if (name === undefined || name in BUILT_IN_STYLES || canvas === undefined) return;
    if (stylesOf(canvas).includes(name)) return;
    const own = stylesOf(canvas);
    check.report(
      "unknown_reference",
      at,
      `No style "${name}" on this canvas. Every canvas has ${Object.keys(BUILT_IN_STYLES).join(", ")}${own.length ? `; this one also has ${own.join(", ")}` : ""}.`,
    );
  };
  const requireWords = (
    words: Animated<string> | undefined,
    values: Values | undefined,
    at: Path,
  ) => {
    if (!values || words === undefined) return;
    for (const said of isTrack(words) ? words.keys.map(key => key.value) : [words]) {
      const read = messageNames(said);
      if ("error" in read) {
        check.report("invalid_proposal", at, `These words are not an ICU message: ${read.error}.`);
        continue;
      }
      for (const name of read.names.filter(name => !(name in values))) {
        check.report(
          "invalid_proposal",
          at,
          `These words name {${name}}, and no value has that name.`,
        );
      }
    }
  };
  const arrangement = surface?.arrangement;
  const { place } = thing as Partial<EntryThing>;
  if (thing.kind === "entry" && arrangement && place !== undefined) {
    const places = isTrack(place)
      ? place.keys.map((key, index) => ({
          value: key.value,
          at: [...path, "place", "keys", index, "value"],
        }))
      : [{ value: place, at: [...path, "place"] }];
    for (const { value, at } of places) {
      if (arrangement.type === "list" && typeof value !== "number") {
        check.report("invalid_proposal", at, "On a list, an entry's place is a number, from 0.");
      } else if (arrangement.type === "grid" && !Array.isArray(value)) {
        check.report(
          "invalid_proposal",
          at,
          "On a grid, an entry's place is its cell: [column, row].",
        );
      } else if (arrangement.type === "grid" && Array.isArray(value)) {
        if (value[0] >= arrangement.columns) {
          check.report(
            "invalid_proposal",
            at,
            `This grid has ${arrangement.columns} columns, from 0.`,
          );
        }
      } else if (arrangement.type === "tree" && Array.isArray(value)) {
        check.report(
          "invalid_proposal",
          at,
          "In a tree, an entry's place is the entry it sits under, or its place among the roots.",
        );
      } else if (arrangement.type === "tree" && typeof value === "string") {
        requireOn(value, ["entry"], at);
      }
    }
  }
  if (thing.kind === "link") {
    const { ends } = thing as Partial<LinkThing>;
    for (const [index, id] of (ends ?? []).entries()) {
      requireOn(id, ["entry"], [...path, "ends", index]);
    }
    if (ends && ends[0] === ends[1]) {
      check.report("invalid_proposal", [...path, "ends"], "A link joins two different entries.");
    }
  }
  if (thing.kind === "point") {
    const point = thing as Partial<PointThing>;
    if (point.scale && point.size && typeof point.size === "object" && "zoom" in point.size) {
      check.report(
        "invalid_proposal",
        [...path, "scale"],
        "A point held to the ground gives its size in pixels, held or keyed, not at chosen zooms.",
      );
    }
  }
  if (thing.kind === "line") {
    const { bend, geometry } = thing as Partial<LineThing>;
    if (geometry && isAround(geometry)) {
      requireOn(geometry.around.point, ["point"], [...path, "geometry", "around", "point"]);
    }
    if (geometry && isEdge(geometry) && bend !== undefined) {
      check.report(
        "invalid_proposal",
        [...path, "bend"],
        "A line along shapes' edges takes its course from them: it has no bend.",
      );
    }
  }
  if (thing.kind === "shape") {
    const { fill, geometry } = thing as Partial<ShapeThing>;
    for (const [index, { point }] of (fill && isCover(fill) ? (fill.clears ?? []) : []).entries()) {
      requireOn(point, ["point"], [...path, "fill", "clears", index, "point"]);
    }
    if (geometry && isTrack(geometry) && !check.committed) {
      requireOrigins(check, geometry, [...path, "geometry"]);
    }
  }
  if (thing.kind === "text" || thing.kind === "card") {
    const where = (thing as Partial<TextThing | CardThing>).on;
    if (where && "point" in where) requireOn(where.point, ["point"], [...path, "on", "point"]);
    if (where && "line" in where) requireOn(where.line, ["line"], [...path, "on", "line"]);
    if (where && "along" in where && typeof where.along === "string") {
      requireOn(where.along, ["line"], [...path, "on", "along"]);
    }
  }
  if (thing.kind === "text") {
    const text = thing as Partial<TextThing>;
    requireStyle(text.style, [...path, "style"]);
    requireWords(text.text, text.values, [...path, "text"]);
    const where = text.on;
    if (placed && where && text.text === undefined) {
      const of = "point" in where ? where.point : "line" in where ? where.line : undefined;
      if (of === undefined || !thingAt(of)?.named) {
        check.report(
          "invalid_proposal",
          [...path, "text"],
          of === undefined
            ? "This text gives no words: only a text on a point or line shows that thing's name."
            : `This text gives no words, and "${of}" has no name to show: give it words.`,
        );
      }
    }
  }
}

/**
 * Every key taking the shape to nothing or bringing it from nothing says where its change starts:
 * with no ground on the other side to spread from or draw back toward, nothing else does.
 */
function requireOrigins(
  check: Check,
  geometry: Exclude<ShapeGeometry, unknown[]>,
  path: Path,
): void {
  if (geometry.ease === "step") return;
  const { keys } = geometry;
  for (let i = 1; i < keys.length; i++) {
    const before = keys[i - 1] as (typeof keys)[number];
    const key = keys[i] as (typeof keys)[number];
    if (key.origin || key.at <= before.at) continue;
    const at = [...path, "keys", i, "origin"];
    if (!key.value.length && before.value.length) {
      check.report(
        "invalid_proposal",
        at,
        "This key takes the shape to nothing with no origin, so its ground would draw back into the middle of each piece. Give it an origin: the points or lines the ground draws back into, such as the line it falls back behind.",
      );
      continue;
    }
    if (key.value.length && !before.value.length) {
      check.report(
        "invalid_proposal",
        at,
        "This key brings the shape from nothing with no origin, so its ground would grow out of the middle of each piece. Give it an origin: the points or lines the ground spreads out from, such as the line it crosses.",
      );
    }
  }
}

/** Every track in a value, however deep, has its keys within its window, in order along the clock. */
function requireTracks(check: Check, value: unknown, window: Window, path: Path): void {
  if (typeof value !== "object" || value === null) return;
  if (Array.isArray(value)) {
    if (typeof value[0] !== "object" || value[0] === null || Array.isArray(value[0])) return;
    for (const [index, item] of value.entries())
      requireTracks(check, item, window, [...path, index]);
    return;
  }
  const record = value as Record<string, unknown>;
  if (Array.isArray(record.keys) && record.keys.every(key => typeof key?.at === "number")) {
    requireKeys(check, record.keys as { at: number }[], window, [...path, "keys"]);
    return;
  }
  for (const [key, inner] of Object.entries(record))
    requireTracks(check, inner, window, [...path, key]);
}

function requireKeys(check: Check, keys: readonly { at: number }[], window: Window, path: Path) {
  let last = Number.NEGATIVE_INFINITY;
  for (const [index, key] of keys.entries()) {
    requireWithin(check, key.at, window, [...path, index, "at"]);
    if (key.at < last) {
      check.report("invalid_proposal", [...path, index, "at"], "Keys go in order along the clock.");
    }
    last = key.at;
  }
}

/** Whose timeline a view's is, for a message. */
const timelineOf = (view: ViewValidationIndex) =>
  isPerspectiveView(view) && view.perspective
    ? `Perspective ${view.perspective.handle}'s timeline`
    : "The public timeline";

/** A round boundary is labelled in every view it is in: a round's date is the label where it begins. */
function requireBoundaryLabel(check: Check, view: ViewValidationIndex, tick: number, path: Path) {
  if ([...view.labels.keys()].some(at => Math.abs(at - tick) < 1e-9)) return;
  check.report(
    "invalid_lifecycle",
    path,
    `${timelineOf(view)} has no label at tick ${tick}, where a round begins: label it there first.`,
  );
}

/** A thing appears and leaves within its window, and appears before it leaves. */
function requireMoments(
  check: Check,
  thing: { from?: number; until?: number },
  window: Window,
  path: Path,
) {
  if (thing.from !== undefined) requireWithin(check, thing.from, window, [...path, "from"]);
  if (thing.until !== undefined) requireWithin(check, thing.until, window, [...path, "until"]);
  if (thing.from !== undefined && thing.until !== undefined && thing.until < thing.from) {
    check.report("invalid_proposal", [...path, "until"], "It leaves before it appears.");
  }
}

/** Where the moments of something written may fall: ticks of the timeline, or a scene's seconds. */
interface Window {
  from: number;
  to: number;
  /** What the window is, for a message, such as "the extension". */
  of: string;
}

function requireWithin(check: Check, at: number, window: Window, path: Path) {
  if (at >= window.from - 1e-9 && at <= window.to + 1e-9) return;
  check.report(
    "invalid_proposal",
    path,
    `${at} is outside ${window.of}, which runs from ${window.from} to ${window.to}.`,
  );
}

function checkClock(check: Check, view: ViewValidationIndex, operation: RoundOperation): void {
  const { clock } = view;
  if (operation.type === "world.started") {
    if (clock) {
      check.report("invalid_lifecycle", ["operation"], "This view has already started.");
    } else if (view.sceneIds.size > 0) {
      check.report("invalid_lifecycle", ["operation"], "This view has already been shown a scene.");
    } else if (isSpectatorView(view) && operation.roundCompleted !== 0) {
      check.report(
        "invalid_lifecycle",
        ["operation", "roundCompleted"],
        "The spectator starts at round boundary 0.",
      );
    } else if (isSpectatorView(view) && operation.tick !== 0) {
      check.report("invalid_lifecycle", ["operation", "tick"], "The spectator starts at tick 0.");
    }
    return;
  }
  if (!clock) {
    check.report("invalid_lifecycle", ["operation"], "This view has not started.");
    return;
  }
  switch (operation.type) {
    case "jump.started":
      if (clock.jumping) {
        check.report("jump_active", ["operation"], "A jump is already active.");
      } else if (operation.round < clock.round) {
        check.report(
          "invalid_lifecycle",
          ["operation", "round"],
          `The view is in round ${clock.round}, so the jump ends round ${clock.round} or later.`,
        );
      }
      requireBoundaryLabel(check, view, clock.tick, ["operation"]);
      return;
    case "jump.completed":
      if (!clock.jumping) {
        check.report("invalid_lifecycle", ["operation"], "No jump is active.");
      } else if (operation.roundCompleted !== clock.round) {
        check.report(
          "invalid_lifecycle",
          ["operation", "roundCompleted"],
          `The active jump ends round ${clock.round}.`,
        );
      }
      if (Math.abs(view.end - operation.tick) > 1e-9) {
        check.report(
          "invalid_lifecycle",
          ["operation", "tick"],
          `${timelineOf(view)} ends at tick ${view.end}, and the jump ends at ${operation.tick}: a jump ends where every timeline it reaches ends.`,
        );
      }
      requireBoundaryLabel(check, view, operation.tick, ["operation", "tick"]);
      return;
  }
}

/** An answer fits the input its perspective waits on; a withdrawal needs one to withdraw. */
function checkInputInView(
  check: Check,
  view: PerspectiveValidationIndex,
  operation: Extract<TimelineOperation, { type: "input.answered" | "input.withdrawn" }>,
): void {
  check.requireJump(view);
  const { awaits } = view;
  if (!awaits) {
    check.report("invalid_lifecycle", ["operation"], "This perspective waits on no input.");
    return;
  }
  if (operation.type === "input.withdrawn") return;
  const { answer } = operation;
  const path = ["operation", "answer"];
  if (answer.type === "skip") {
    if (!awaits.skippable) check.report("invalid_lifecycle", path, "This input cannot be skipped.");
    return;
  }
  if (awaits.type !== "choice") {
    check.report("invalid_lifecycle", path, "A reply is answered by writing in its thread.");
    return;
  }
  if (answer.option !== undefined && !(awaits.options ?? []).includes(answer.option)) {
    check.report(
      "invalid_lifecycle",
      [...path, "option"],
      "The answer must be one of the choice's options.",
    );
  }
  if (answer.text !== undefined && awaits.freeform !== true) {
    check.report("invalid_lifecycle", [...path, "text"], "This choice takes only its options.");
  }
}

function checkThreadInView(
  check: Check,
  view: PerspectiveValidationIndex,
  operation: ThreadOperation,
): void {
  const path = ["operation"];
  if (operation.type === "thread.pinsSet") {
    for (const [index, threadId] of operation.threadIds.entries()) {
      if (view.threads.has(threadId)) continue;
      check.report(
        "unknown_reference",
        [...path, "threadIds", index],
        `Thread "${threadId}" does not exist.`,
      );
    }
    return;
  }
  if (operation.type === "thread.create") {
    check.requireEventId(view, operation.eventId, ["operation", "eventId"]);
    check.requireNew(view.threads, operation.thread.id, [...path, "thread", "id"], "Thread");
    const { frame } = operation.thread;
    if (frame) check.requireCanvas(view, frame.canvasId, [...path, "thread", "frame", "canvasId"]);
    return;
  }
  const threadId = operation.type === "thread.update" ? operation.id : operation.threadId;
  const thread = check.requireExisting(view.threads, threadId, [...path, "threadId"], "Thread");
  if (!thread) return;

  switch (operation.type) {
    case "thread.append": {
      check.requireEventId(view, operation.eventId, ["operation", "eventId"]);
      if (thread.status !== "open") {
        check.report("invalid_lifecycle", [...path, "threadId"], "The thread is archived.");
      }
      const { entry } = operation;
      check.requireNew(thread.entryIds, entry.id, [...path, "entry", "id"], "Entry");
      if (entry.presentation.type !== "system") {
        requireSpeaker(check, thread, entry.presentation, [...path, "entry", "presentation"]);
      }
      if (entry.scene) checkShownScene(check, view, entry.scene, [...path, "entry", "scene"]);
      if (entry.content.type === "poll") {
        check.requireNew(
          thread.polls,
          entry.content.pollId,
          [...path, "entry", "content", "pollId"],
          "Poll",
        );
      }
      if (entry.content.type === "request") {
        const { request } = entry.content;
        const requestPath = [...path, "entry", "content", "request"];
        if (entry.presentation.type !== "perspective") {
          check.report(
            "wrong_actor",
            [...path, "entry", "presentation"],
            "A request is presented as the perspective.",
          );
        }
        if (request.kind === "askOut") {
          requireSpeaker(check, thread, request.speaker, [...requestPath, "speaker"]);
        }
      }
      return;
    }
    case "thread.update": {
      check.requireEventId(view, operation.eventId, ["operation", "eventId"]);
      const { frame } = operation.changes;
      if (frame)
        check.requireCanvas(view, frame.canvasId, [...path, "changes", "frame", "canvasId"]);
      const { awaits } = view;
      if (
        operation.changes.status === "archived" &&
        awaits?.type === "reply" &&
        awaits.threadId === threadId
      ) {
        check.report(
          "invalid_lifecycle",
          [...path, "changes", "status"],
          "The perspective waits on a reply in this thread.",
        );
      }
      for (const [index, change] of (operation.changes.members ?? []).entries()) {
        const at = [...path, "changes", "members", index];
        if (change.type === "append") {
          if (thread.members.get(change.member.id)?.listed) {
            check.report(
              "already_exists",
              [...at, "member", "id"],
              `"${change.member.id}" is already listed in this thread.`,
            );
          }
          continue;
        }
        const member = thread.members.get(change.id);
        if (!member || (change.type === "remove" && !member.listed)) {
          check.report(
            "unknown_reference",
            [...at, "id"],
            change.type === "remove"
              ? `"${change.id}" is not listed in this thread.`
              : `"${change.id}" is not a member of this thread.`,
          );
        }
      }
      return;
    }
    case "thread.pollResponded":
    case "thread.pollClosed": {
      const poll = check.requireExisting(
        thread.polls,
        operation.pollId,
        [...path, "pollId"],
        "Poll",
      );
      if (!poll) return;
      if (poll.status !== "open") {
        check.report("invalid_lifecycle", [...path, "pollId"], "The poll is closed.");
      }
      if (operation.type === "thread.pollClosed") return;
      const { respondent, response } = operation;
      requireSpeaker(check, thread, respondent, [...path, "respondent"]);
      if ("optionId" in response && !poll.optionIds.has(response.optionId)) {
        check.report(
          "unknown_reference",
          [...path, "response", "optionId"],
          `Option "${response.optionId}" is not on this poll.`,
        );
      }
      if ("text" in response && !poll.freeform) {
        check.report(
          "invalid_lifecycle",
          [...path, "response", "text"],
          "This poll takes only its options.",
        );
      }
      return;
    }
    case "thread.reactionSet":
    case "thread.reactionUnset": {
      const { entryId, reactor, mark } = operation;
      if (!thread.messageIds.has(entryId)) {
        check.report(
          "unknown_reference",
          [...path, "entryId"],
          `"${entryId}" is not a message in this thread.`,
        );
      }
      requireSpeaker(check, thread, reactor, [...path, "reactor"]);
      const reacted = thread.reactions.has(reactionKey(entryId, mark, reactor));
      if (operation.type === "thread.reactionSet") {
        if (thread.status !== "open") {
          check.report("invalid_lifecycle", [...path, "threadId"], "The thread is archived.");
        }
        if (reacted) {
          check.report("invalid_lifecycle", [...path, "mark"], "This mark is already left.");
        }
      } else if (!reacted) {
        check.report("invalid_lifecycle", [...path, "mark"], "There is no such mark to take back.");
      }
      return;
    }
  }
}

/** A member speaker must be one of the thread's members, listed or removed. */
function requireSpeaker(
  check: Check,
  thread: ThreadValidationEntry,
  speaker: ThreadSpeaker,
  path: Path,
): void {
  if (speaker.type === "member" && !thread.members.has(speaker.memberId)) {
    check.report(
      "unknown_reference",
      [...path, "memberId"],
      `"${speaker.memberId}" is not a member of this thread.`,
    );
  }
}

function checkActionInView(
  check: Check,
  view: PerspectiveValidationIndex,
  operation: ActionOperation,
): void {
  check.requireNoJump(view);
  if (operation.type === "action.submitted") {
    check.requireNew(view.actions, operation.action.id, ["operation", "action", "id"], "Action");
    return;
  }
  const action = check.requireExisting(view.actions, operation.id, ["operation", "id"], "Action");
  if (action && action.status !== "pending") {
    check.report(
      "invalid_lifecycle",
      ["operation", "id"],
      `The action is ${action.status}, not pending.`,
    );
  }
}

function checkBoardInView(
  check: Check,
  view: ViewValidationIndex,
  operation: BoardOperation,
): void {
  const path = ["operation"];
  const { arrangement } = view.things.get(operation.canvasId) ?? {};
  if (!arrangement) {
    check.report(
      "unknown_reference",
      [...path, "canvasId"],
      `No board "${operation.canvasId}" on this view's map.`,
    );
    return;
  }
  if (arrangement.type === "tree") {
    check.report(
      "invalid_proposal",
      [...path, "canvasId"],
      "A tree is laid out from the entry each sits under: only a list or a grid is arranged.",
    );
    return;
  }
  for (const [id, place] of Object.entries(operation.places)) {
    const at = [...path, "places", id];
    const entry = view.things.get(id);
    if (entry?.kind !== "entry" || entry.canvas !== operation.canvasId) {
      check.report("unknown_reference", at, `No entry "${id}" on this board.`);
    } else if (arrangement.type === "list" && typeof place !== "number") {
      check.report("invalid_proposal", at, "On a list, a place is a number, from 0.");
    } else if (arrangement.type === "grid" && !Array.isArray(place)) {
      check.report("invalid_proposal", at, "On a grid, a place is a cell: [column, row].");
    } else if (arrangement.type === "grid" && Array.isArray(place)) {
      if (place[0] >= arrangement.columns) {
        check.report(
          "invalid_proposal",
          at,
          `This grid has ${arrangement.columns} columns, from 0.`,
        );
      }
    }
  }
}

// Whole-record validation: the rules that read the actor, the responsible user, the target,
// seating, or any view. They only ever reject.

class WholeRecordCheck extends Check {
  constructor(
    readonly indexes: ValidationIndexes,
    readonly delta: ProposedDelta,
  ) {
    super(isCommitted(delta));
  }

  get actor(): Actor {
    return this.delta.actor;
  }

  isAuthority(): boolean {
    return this.actor.type === "gameMaster" || this.actor.type === "system";
  }

  requireAuthority(): boolean {
    if (this.isAuthority()) return true;
    this.report("wrong_actor", ["actor"], "Only the game master may propose this operation.");
    return false;
  }

  /**
   * Every view the target reaches, each once. Each view it names must exist, and each
   * distribution list must exist and reach at least one perspective.
   */
  views(options: { allowSpectator: boolean }): ViewValidationIndex[] | null {
    const { target } = this.delta;
    if (target.type !== "perspectives") {
      this.report("invalid_target", ["target"], "This operation must target perspectives.");
      return null;
    }
    const named = new Set<string>();
    for (const [index, ref] of target.perspectives.entries()) {
      const path = ["target", "perspectives", index];
      const key = ref.type === "distributionList" ? `distributionList:${ref.id}` : viewKey(ref);
      if (named.has(key)) {
        this.report("invalid_target", path, "The target names this reference twice.");
        return null;
      }
      named.add(key);
      if (ref.type === "distributionList") {
        const members = this.indexes.wholeRecord.distributionLists.get(ref.id);
        if (!members) {
          this.report("invalid_target", path, `Distribution list "${ref.id}" does not exist.`);
          return null;
        }
        if (![...members].some(handle => this.indexes.perspectives.has(handle))) {
          this.report("invalid_target", path, `Distribution list "${ref.id}" reaches nobody.`);
          return null;
        }
        continue;
      }
      if (ref.type === "spectator" && !options.allowSpectator) {
        this.report("invalid_target", path, "The spectator cannot receive this operation.");
        return null;
      }
      if (!viewFor(this.indexes, ref)) {
        this.report("invalid_target", path, `Perspective "${viewKeyId(key)}" does not exist.`);
        return null;
      }
    }
    return reachedViewRefs(this.indexes, target).flatMap(ref => viewFor(this.indexes, ref) ?? []);
  }

  perspectiveViews(): PerspectiveValidationIndex[] | null {
    return this.views({ allowSpectator: false }) as PerspectiveValidationIndex[] | null;
  }

  /** The perspective a user is on, or null for the spectator. */
  seatOf(userId: string): string | null {
    return this.indexes.wholeRecord.userPerspectives.get(userId) ?? null;
  }

  /** A perspective actor may only address its own perspective, for a user on it. */
  requireOwnPerspective(): PerspectiveValidationIndex | null {
    const { actor } = this;
    const { target } = this.delta;
    if (actor.type !== "perspective") return null;
    const [only, ...rest] = target.type === "perspectives" ? target.perspectives : [];
    if (!only || rest.length > 0 || only.type !== "perspective" || only.handle !== actor.handle) {
      this.report("wrong_actor", ["target"], "A perspective may only address itself.");
      return null;
    }
    return this.actingPerspective();
  }

  /** The actor's perspective, which must exist and have the responsible user on it. */
  actingPerspective(): PerspectiveValidationIndex | null {
    const { actor } = this;
    const { responsibleUser } = this.delta;
    if (actor.type !== "perspective") return null;
    const perspective = this.indexes.perspectives.get(actor.handle);
    if (!perspective) {
      this.report("invalid_target", ["target"], `Perspective "${actor.handle}" does not exist.`);
      return null;
    }
    if (!responsibleUser || !perspective.perspective?.userIds.has(responsibleUser)) {
      this.report("wrong_actor", ["responsibleUser"], "This user is not on the perspective.");
      return null;
    }
    return perspective;
  }
}

function viewKeyId(key: string): string {
  return key.slice(key.indexOf(":") + 1);
}

function checkInWholeRecord(check: WholeRecordCheck, operation: Operation): void {
  if (!check.isAuthority() && "eventId" in operation && operation.eventId !== undefined) {
    check.report(
      "wrong_actor",
      ["operation", "eventId"],
      "Only the game master or the system ties a change to an event.",
    );
    return;
  }
  const { target } = check.delta;
  if (target.type === "users" && new Set(target.userIds).size < target.userIds.length) {
    check.report("invalid_target", ["target", "userIds"], "The target names a user twice.");
    return;
  }
  if (isRoundOperation(operation)) {
    checkRoundInWholeRecord(check, operation);
    return;
  }
  switch (operation.type) {
    case "perspective.set":
    case "perspective.update":
    case "perspective.updateUsers":
    case "perspective.unset":
      checkPerspectiveInWholeRecord(check, operation);
      return;
    case "jump.requested":
    case "jump.requestWithdrawn":
      if (check.actor.type !== "perspective") {
        check.report("wrong_actor", ["actor"], "Only a perspective asks for a jump.");
        return;
      }
      check.requireOwnPerspective();
      return;
    case "perspectiveSuggestion.set":
    case "perspectiveSuggestion.update":
    case "perspectiveSuggestion.unset": {
      if (!check.requireAuthority()) return;
      const [only, ...rest] = target.type === "perspectives" ? target.perspectives : [];
      if (!only || rest.length > 0 || only.type !== "spectator") {
        check.report(
          "invalid_target",
          ["target"],
          "Perspective suggestions target the spectator alone.",
        );
      }
      return;
    }
    case "perspectiveChange.requested":
    case "perspectiveChange.withdrawn":
    case "perspectiveChange.approved":
    case "perspectiveChange.refused":
      checkPerspectiveChangeInWholeRecord(check, operation);
      return;
    case "alias.set":
    case "alias.unset":
      if (check.requireAuthority()) check.views({ allowSpectator: true });
      return;
    case "timeline.extended":
    case "timeline.clarified":
    case "scene.shown":
      if (check.requireAuthority()) check.views({ allowSpectator: true });
      return;
    case "input.asked":
      if (check.requireAuthority()) check.perspectiveViews();
      return;
    case "distributionList.set":
    case "distributionList.update":
    case "distributionList.perspectiveAdded":
    case "distributionList.perspectiveRemoved":
    case "distributionList.unset":
      checkDistributionListInWholeRecord(check, operation);
      return;
    case "input.withdrawn":
      if (check.requireAuthority()) check.perspectiveViews();
      return;
    case "input.answered":
      if (check.actor.type !== "perspective") {
        check.report("wrong_actor", ["actor"], "Only a perspective answers its input.");
        return;
      }
      check.requireOwnPerspective();
      return;
    case "jump.intervened":
      if (check.actor.type !== "perspective") {
        check.report("wrong_actor", ["actor"], "Only a perspective intervenes.");
        return;
      }
      check.requireOwnPerspective();
      return;
    case "thread.create":
    case "thread.append":
    case "thread.update":
    case "thread.pollResponded":
    case "thread.pollClosed":
    case "thread.reactionSet":
    case "thread.reactionUnset":
    case "thread.pinsSet":
      checkThreadInWholeRecord(check, operation);
      return;
    case "action.submitted":
    case "action.edited":
    case "action.withdrawn":
      checkActionInWholeRecord(check);
      return;
    case "board.arranged":
      if (check.actor.type !== "perspective") {
        check.report("wrong_actor", ["actor"], "Only a perspective arranges its boards.");
        return;
      }
      check.requireOwnPerspective();
      return;
    case "advisor.entryAdded":
    case "advisor.cleared":
      checkAdvisorInWholeRecord(check, operation);
      return;
    case "skill.set":
    case "skill.update":
    case "skill.unset":
      checkSkillInWholeRecord(check, operation);
      return;
    case "gameMaster.assigned":
    case "gameMaster.unassigned":
      checkGameMasterInWholeRecord(check, operation);
      return;
  }
}

function checkPerspectiveInWholeRecord(
  check: WholeRecordCheck,
  operation: PerspectiveOperation,
): void {
  if (!check.requireAuthority()) return;
  if (operation.type === "perspective.update" || operation.type === "perspective.unset") {
    check.perspectiveViews();
    return;
  }
  if (operation.type === "perspective.updateUsers") {
    checkUsersInWholeRecord(check, operation.userIds);
    return;
  }
  const { handle } = operation.perspective;
  const { target } = check.delta;
  const [only, ...rest] = target.type === "perspectives" ? target.perspectives : [];
  if (!only || rest.length > 0 || only.type !== "perspective" || only.handle !== handle) {
    check.report(
      "invalid_target",
      ["target"],
      "A perspective operation targets that perspective alone.",
    );
    return;
  }
  const { wholeRecord, perspectives } = check.indexes;
  if (wholeRecord.handles.has(handle)) {
    check.report(
      "already_exists",
      ["operation", "perspective", "handle"],
      `Handle "${handle}" has been used before, by this or a removed perspective.`,
    );
  }
  const { fork } = operation;
  if (fork?.type === "perspective" && !perspectives.has(fork.handle)) {
    check.report(
      "unknown_reference",
      ["operation", "fork", "handle"],
      `Perspective "${fork.handle}" does not exist.`,
    );
  }
}

/**
 * The users go on every perspective the target reaches. Each is on one perspective at most, and
 * joins only in answer to their own ask, while the perspective is in sync with the spectator.
 */
function checkUsersInWholeRecord(check: WholeRecordCheck, userIds: readonly string[]): void {
  const views = check.perspectiveViews();
  if (!views) return;
  if (views.length > 1 && userIds.length > 0) {
    check.report(
      "invalid_target",
      ["target"],
      `A user is on one perspective at most, and this would put the users on ${views.length}.`,
    );
    return;
  }
  for (const view of views) {
    if (!view.perspective) continue;
    const { handle, userIds: current } = view.perspective;
    const joining = userIds.filter(userId => !current.has(userId));
    if (joining.length > 0 && !inSync(view.clock, check.indexes.spectator.clock)) {
      check.report(
        "invalid_lifecycle",
        ["operation", "userIds"],
        "A user joins a perspective only while it is in sync with the spectator: the same round, begun at the same tick. Bring it up to date first.",
      );
    }
    for (const [index, userId] of userIds.entries()) {
      if (current.has(userId)) continue;
      const path = ["operation", "userIds", index];
      const seat = check.seatOf(userId);
      if (seat !== null && seat !== handle) {
        check.report(
          "already_exists",
          path,
          `User "${userId}" is on perspective "${seat}". Take them off it first.`,
        );
      }
      const ask = check.indexes.users.get(userId)?.perspectiveChange;
      if (!ask || ask.to === "spectator") {
        check.report(
          "invalid_lifecycle",
          path,
          `User "${userId}" has not asked to play. A user joins a perspective only in answer to their own ask.`,
        );
      }
    }
  }
}

/** Whether a perspective's clock matches the spectator's, neither having started counting too. */
function inSync(perspective: Clock | null, spectator: Clock | null): boolean {
  if (!perspective || !spectator) return perspective === spectator;
  return (
    perspective.round === spectator.round &&
    perspective.jumping === spectator.jumping &&
    perspective.tick === spectator.tick
  );
}

/**
 * The game master runs the world's jumps for the active views. The world's jump starts only once a
 * perspective with users has asked. It also backfills perspectives it names: a backfill jump may
 * skip rounds but never runs ahead of the world, and it ends on a round boundary the spectator has
 * passed, as a backfill's `world.started` starts on one.
 */
function checkRoundInWholeRecord(check: WholeRecordCheck, operation: RoundOperation): void {
  if (!check.requireAuthority()) return;
  const { spectator, perspectives } = check.indexes;
  const world = spectator.clock;
  if (check.delta.target.type === "activeViews") {
    if (operation.type === "jump.completed") {
      for (const [handle, view] of perspectives) {
        if (!view.awaits) continue;
        check.report(
          "invalid_lifecycle",
          ["operation"],
          `"${handle}" still waits on an input: it is answered or withdrawn before the jump ends.`,
        );
      }
      return;
    }
    if (operation.type !== "jump.started" || !world) return;
    if (operation.round !== world.round) {
      check.report(
        "invalid_lifecycle",
        ["operation", "round"],
        `The world's jump ends the current round, ${world.round}.`,
      );
    } else if (![...perspectives.values()].some(view => hasUsers(view) && view.jumpRequest)) {
      check.report(
        "invalid_lifecycle",
        ["operation"],
        "No perspective with users has asked for a jump.",
      );
    }
    return;
  }
  if (!check.perspectiveViews()) return;
  switch (operation.type) {
    case "world.started":
    case "jump.completed":
      requireSpectatorBoundary(check, operation);
      return;
    case "jump.started":
      if (
        world &&
        (operation.round > world.round || (operation.round === world.round && !world.jumping))
      ) {
        check.report(
          "invalid_lifecycle",
          ["operation", "round"],
          "A backfill never runs ahead of the world: the world has not started the jump that ends this round.",
        );
      }
      return;
  }
}

function requireSpectatorBoundary(
  check: WholeRecordCheck,
  operation: { roundCompleted: number; tick: number },
): void {
  const passed = check.indexes.wholeRecord.boundaries.some(
    boundary =>
      boundary.roundCompleted === operation.roundCompleted && boundary.tick === operation.tick,
  );
  if (!passed) {
    check.report(
      "invalid_lifecycle",
      ["operation"],
      "This is not a round boundary the spectator has passed.",
    );
  }
}

/**
 * A user asks for themselves and may take back only their own unanswered ask. The game master
 * answers it, approving once the user is where they asked to be, as the spectator or in a
 * perspective.
 */
function checkPerspectiveChangeInWholeRecord(
  check: WholeRecordCheck,
  operation: PerspectiveChangeOperation,
): void {
  const { target, responsibleUser } = check.delta;
  if (target.type !== "users" || target.userIds.length !== 1) {
    check.report("invalid_target", ["target"], "A perspective change targets its user alone.");
    return;
  }
  const [userId] = target.userIds;
  const seat = check.seatOf(userId);

  if (
    operation.type === "perspectiveChange.requested" ||
    operation.type === "perspectiveChange.withdrawn"
  ) {
    const { actor } = check;
    if (actor.type !== "user" || actor.userId !== userId || responsibleUser !== userId) {
      check.report("wrong_actor", ["actor"], "A user asks to change only their own perspective.");
      return;
    }
    if (operation.type === "perspectiveChange.withdrawn") return;
    const { to } = operation;
    const { spectator } = check.indexes;
    if (to.type === "spectator") {
      if (seat === null) {
        check.report(
          "invalid_lifecycle",
          ["operation", "to"],
          "This user is already the spectator.",
        );
      }
      return;
    }
    if (to.type === "suggestion") {
      if (!spectator.perspectiveSuggestions.has(to.suggestionId)) {
        check.report(
          "unknown_reference",
          ["operation", "to", "suggestionId"],
          `Perspective suggestion "${to.suggestionId}" is not offered.`,
        );
      }
      return;
    }
    if (to.type === "perspective") {
      if (!check.indexes.perspectives.has(to.handle)) {
        check.report(
          "unknown_reference",
          ["operation", "to", "handle"],
          `Perspective "${to.handle}" does not exist.`,
        );
      }
      return;
    }
    check.requireCanvas(spectator, to.startingView.canvasId, [
      "operation",
      "to",
      "startingView",
      "canvasId",
    ]);
    check.requireShapes(spectator, to.shapeIds ?? [], ["operation", "to", "shapeIds"]);
    return;
  }

  if (!check.requireAuthority() || operation.type !== "perspectiveChange.approved") return;
  const pending = check.indexes.users.get(userId)?.perspectiveChange;
  if (pending?.id !== operation.id) return;
  if (pending.to === "spectator" && seat !== null) {
    check.report(
      "invalid_lifecycle",
      ["operation"],
      `Take the user out of perspective "${seat}" first.`,
    );
  } else if (pending.to !== "spectator" && seat === null) {
    check.report("invalid_lifecycle", ["operation"], "Put the user in a perspective first.");
  }
}

function checkDistributionListInWholeRecord(
  check: WholeRecordCheck,
  operation: DistributionListOperation,
): void {
  if (!check.requireAuthority()) return;
  if (check.delta.target.type !== "gameMaster") {
    check.report("invalid_target", ["target"], "Distribution lists target the game master.");
    return;
  }
  const lists = check.indexes.wholeRecord.distributionLists;
  if (operation.type === "distributionList.set") {
    const { id, handles } = operation.distributionList;
    check.requireNew(lists, id, ["operation", "distributionList", "id"], "Distribution list");
    requireListPerspectives(check, handles, ["operation", "distributionList", "handles"]);
    return;
  }
  const members = lists.get(operation.id);
  if (!members) {
    check.report(
      "unknown_reference",
      ["operation", "id"],
      `Distribution list "${operation.id}" does not exist.`,
    );
    return;
  }
  const perspectivePath = ["operation", "handle"];
  switch (operation.type) {
    case "distributionList.update":
      if (operation.changes.handles) {
        requireListPerspectives(check, operation.changes.handles, [
          "operation",
          "changes",
          "handles",
        ]);
      }
      return;
    case "distributionList.perspectiveAdded":
      if (!check.indexes.perspectives.has(operation.handle)) {
        check.report(
          "unknown_reference",
          perspectivePath,
          `Perspective "${operation.handle}" does not exist.`,
        );
      } else if (members.has(operation.handle)) {
        check.report("already_exists", perspectivePath, "The perspective is already on the list.");
      }
      return;
    case "distributionList.perspectiveRemoved":
      if (!members.has(operation.handle)) {
        check.report("unknown_reference", perspectivePath, "The perspective is not on the list.");
      }
      return;
  }
}

function requireListPerspectives(
  check: WholeRecordCheck,
  perspectiveHandles: readonly string[],
  path: Path,
): void {
  const seen = new Set<string>();
  for (const [index, perspectiveHandle] of perspectiveHandles.entries()) {
    if (!check.indexes.perspectives.has(perspectiveHandle) || seen.has(perspectiveHandle)) {
      check.report(
        "unknown_reference",
        [...path, index],
        `Perspective "${perspectiveHandle}" does not exist, or is listed twice.`,
      );
    }
    seen.add(perspectiveHandle);
  }
}

/**
 * The game master writes threads for any perspective, as anyone. A perspective writes only in its
 * own, as itself, and during a jump only in the thread whose reply it waits on.
 */
function checkThreadInWholeRecord(check: WholeRecordCheck, operation: ThreadOperation): void {
  const isPerspective = check.actor.type === "perspective";
  if (operation.type === "thread.pinsSet") {
    if (!isPerspective) {
      check.report("wrong_actor", ["actor"], "Only a perspective pins its threads.");
      return;
    }
    check.requireOwnPerspective();
    return;
  }
  if (!isPerspective) {
    if (!check.requireAuthority() || !check.perspectiveViews()) return;
    const speaker =
      operation.type === "thread.pollResponded"
        ? { speaker: operation.respondent, field: "respondent" }
        : operation.type === "thread.reactionSet" || operation.type === "thread.reactionUnset"
          ? { speaker: operation.reactor, field: "reactor" }
          : null;
    if (speaker?.speaker.type === "perspective") {
      check.report(
        "wrong_actor",
        ["operation", speaker.field],
        "The game master speaks as anyone but the perspective.",
      );
    }
    return;
  }
  if (operation.type === "thread.update" || operation.type === "thread.pollClosed") {
    check.report("wrong_actor", ["actor"], "Only the game master may propose this operation.");
    return;
  }
  const perspective = check.requireOwnPerspective();
  if (!perspective) return;
  const { awaits } = perspective;
  const answersInput =
    "threadId" in operation && awaits?.type === "reply" && awaits.threadId === operation.threadId;
  const jumping = perspective.clock?.jumping === true;
  switch (operation.type) {
    case "thread.create":
      check.requireNoJump(perspective);
      return;
    case "thread.append":
      if (operation.entry.presentation.type !== "perspective") {
        check.report(
          "wrong_actor",
          ["operation", "entry", "presentation"],
          "A perspective presents entries only as itself.",
        );
      }
      if (jumping && !answersInput) {
        check.report(
          "jump_active",
          [],
          "During a jump, a perspective may only write in the thread whose reply it waits on.",
        );
      }
      return;
    case "thread.pollResponded":
      check.requireNoJump(perspective);
      if (operation.respondent.type !== "perspective") {
        check.report(
          "wrong_actor",
          ["operation", "respondent"],
          "A perspective responds only as itself.",
        );
      }
      return;
    case "thread.reactionSet":
    case "thread.reactionUnset":
      if (operation.reactor.type !== "perspective") {
        check.report(
          "wrong_actor",
          ["operation", "reactor"],
          "A perspective reacts only as itself.",
        );
      }
      if (jumping && !answersInput) {
        check.report(
          "jump_active",
          [],
          "During a jump, a perspective may only react in the thread whose reply it waits on.",
        );
      }
      return;
  }
}

/** A perspective acts on its own actions; the game master and the system on any perspective's. */
function checkActionInWholeRecord(check: WholeRecordCheck): void {
  if (check.isAuthority()) {
    check.perspectiveViews();
    return;
  }
  if (check.actor.type !== "perspective") {
    check.report("wrong_actor", ["actor"], "Only a perspective or the game master acts.");
    return;
  }
  check.requireOwnPerspective();
}

function checkAdvisorInWholeRecord(check: WholeRecordCheck, operation: AdvisorOperation): void {
  const { target } = check.delta;
  if (target.type !== "users") {
    check.report("invalid_target", ["target"], "Advisor operations target users.");
    return;
  }
  const { actor } = check;
  const [only, ...rest] = target.userIds;
  if (operation.type === "advisor.cleared") {
    if (actor.type === "user" && (rest.length > 0 || only !== actor.userId)) {
      check.report("invalid_target", ["target"], "A user clears only their own advisor.");
    } else if (actor.type !== "user") {
      check.requireAuthority();
    }
    return;
  }
  const { entry } = operation;
  if (entry.from === "user") {
    if (actor.type !== "user") {
      check.report("wrong_actor", ["actor"], "Only a user writes a user entry.");
      return;
    }
    if (rest.length > 0 || only !== actor.userId) {
      check.report("invalid_target", ["target"], "A user writes only to their own advisor.");
      return;
    }
  } else if (!check.requireAuthority()) {
    return;
  }
  for (const [index, userId] of target.userIds.entries()) {
    const seat = check.seatOf(userId);
    if (seat === null) {
      check.report(
        "invalid_target",
        ["target", "userIds", index],
        `User "${userId}" is not on a perspective.`,
      );
      continue;
    }
    const view = viewFor(check.indexes, { type: "perspective", handle: seat });
    if (entry.from === "advisor" && entry.scene && view) {
      checkShownScene(check, view, entry.scene, ["operation", "entry", "scene"]);
    }
  }
}

function checkSkillInWholeRecord(check: WholeRecordCheck, operation: SkillOperation): void {
  if (!check.requireAuthority()) return;
  if (check.delta.target.type !== "gameMaster") {
    check.report("invalid_target", ["target"], "Skills target the game master.");
    return;
  }
  const { skills } = check.indexes.wholeRecord;
  if (operation.type === "skill.set") {
    check.requireNew(skills, operation.skill.id, ["operation", "skill", "id"], "Skill");
  } else if (!skills.has(operation.id)) {
    check.report(
      "unknown_reference",
      ["operation", "id"],
      `Skill "${operation.id}" does not exist.`,
    );
  }
}

function checkGameMasterInWholeRecord(
  check: WholeRecordCheck,
  operation: GameMasterOperation,
): void {
  if (check.actor.type !== "system") {
    check.report(
      "wrong_actor",
      ["actor"],
      "Only the system changes who holds the game master role.",
    );
    return;
  }
  if (check.delta.target.type !== "gameMaster") {
    check.report("invalid_target", ["target"], "Game master changes target the game master.");
    return;
  }
  const { gameMaster, gameMasterAgentIds } = check.indexes.wholeRecord;
  if (operation.type === "gameMaster.unassigned") {
    if (!gameMaster) {
      check.report("invalid_lifecycle", ["operation"], "Nobody holds the game master role.");
    }
    return;
  }
  if (gameMasterAgentIds.has(operation.agentId)) {
    check.report(
      "already_exists",
      ["operation", "agentId"],
      `Agent "${operation.agentId}" has held the game master role before.`,
    );
  }
}
